import React, { useEffect, useState } from 'react';

export type MobileHeaderData = {
  avatarUrl?: string | null;
  profileLabel?: string;
  profileAriaLabel?: string;
  profileButtonId?: string;
  filtersLabel?: string;
  filtersAriaLabel?: string;
  filtersButtonId?: string;
  showProfile?: boolean;
  showFilters?: boolean;
  onProfileClick?: () => void;
  onFiltersClick?: () => void;
};

type Props = {
  data: MobileHeaderData;
};

function UserIcon() {
  return (
    <svg
      className="mobile-header__profile-icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <circle cx="12" cy="10" r="3.25" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M5.5 19.25c1.2-2.6 3.7-4.25 6.5-4.25s5.3 1.65 6.5 4.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function FiltersIcon() {
  return (
    <svg
      className="mobile-header__filters-icon"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M3 5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15 5h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="14" cy="5" r="1.75" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M3 10h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M7 10h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="6" cy="10" r="1.75" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M13 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="15" r="1.75" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

type ProfileButtonProps = {
  id?: string;
  avatarUrl?: string | null;
  label?: string;
  ariaLabel?: string;
  onClick?: () => void;
};

function ProfileButton({ id, avatarUrl, label, ariaLabel, onClick }: ProfileButtonProps) {
  const resolvedAriaLabel = ariaLabel || label || 'Profile';
  // Only render <img> after a successful probe — truthy image_url can still
  // 404, and rendering it first flashes the browser broken-image icon.
  const [avatarOk, setAvatarOk] = useState(false);

  useEffect(() => {
    if (!avatarUrl) {
      setAvatarOk(false);
      return;
    }
    setAvatarOk(false);
    const probe = new window.Image();
    let cancelled = false;
    probe.onload = () => {
      if (!cancelled && probe.naturalWidth > 0) {
        setAvatarOk(true);
      }
    };
    probe.onerror = () => {
      if (!cancelled) {
        setAvatarOk(false);
      }
    };
    probe.src = avatarUrl;
    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [avatarUrl]);

  const showAvatar = !!avatarUrl && avatarOk;

  return (
    <button
      type="button"
      id={id || undefined}
      className="mobile-header__profile"
      onClick={onClick}
      aria-label={resolvedAriaLabel}
      data-testid="mobile-header-profile"
    >
      {showAvatar ? (
        <img className="mobile-header__avatar" src={avatarUrl} alt="" aria-hidden="true" />
      ) : (
        <UserIcon />
      )}
      {label ? <span className="mobile-header__profile-label">{label}</span> : null}
    </button>
  );
}

type FiltersButtonProps = {
  id?: string;
  label?: string;
  ariaLabel?: string;
  onClick?: () => void;
};

function FiltersButton({ id, label, ariaLabel, onClick }: FiltersButtonProps) {
  const resolvedAriaLabel = ariaLabel || label || 'Filters';
  return (
    <button
      type="button"
      id={id || undefined}
      className="mobile-header__filters"
      onClick={onClick}
      aria-label={resolvedAriaLabel}
      data-testid="mobile-header-filters"
    >
      {label ? <span className="mobile-header__filters-label">{label}</span> : null}
      <FiltersIcon />
    </button>
  );
}

export function MobileHeader({ data }: Props) {
  if (!data) {
    return null;
  }

  const {
    avatarUrl,
    profileLabel,
    profileAriaLabel,
    profileButtonId,
    filtersLabel,
    filtersAriaLabel,
    filtersButtonId,
    showProfile = true,
    showFilters = false,
    onProfileClick,
    onFiltersClick,
  } = data;

  return (
    <header className="mobile-header" role="banner">
      {showProfile ? (
        <ProfileButton
          id={profileButtonId}
          avatarUrl={avatarUrl}
          label={profileLabel}
          ariaLabel={profileAriaLabel}
          onClick={onProfileClick}
        />
      ) : (
        <span className="mobile-header__filters-placeholder" aria-hidden="true" />
      )}

      {showFilters ? (
        <FiltersButton
          id={filtersButtonId}
          label={filtersLabel}
          ariaLabel={filtersAriaLabel}
          onClick={onFiltersClick}
        />
      ) : (
        <span className="mobile-header__filters-placeholder" aria-hidden="true" />
      )}
    </header>
  );
}
